import { FormattedMessage, useIntl } from 'react-intl';
import { useTrackRender } from '@repo/shared-kit/hooks/use-track-render';
import cx from 'clsx';
import { BadgeHighlight } from '@repo/design-system-kit/components/badge/highlight/highlight';
import { Button } from '@repo/design-system-kit/components/button/button';
import IconHeadsetOutlined from '@repo/monochrome-icons/icon-headset-outlined';
import IconScanOutlined from '@repo/monochrome-icons/icon-scan-outlined';
import { ComputerVerification, PhoneVerification } from '../../svgs';
import { BackButtonSimple } from '../../shared/back-button-simple';
import styles from './landing-desktop.module.css';

function Divider(): React.JSX.Element {
  return (
    <div className={styles.cardDivider}>
      <div className={styles.cardDividerLine} />
      <div className="body-standard">
        <FormattedMessage
          defaultMessage="or"
          id="identity-verification-kit.qonto-idnow.desktop.switch-to-mobile.separator"
        />
      </div>
      <div className={styles.cardDividerLine} />
    </div>
  );
}

function CardItem({
  icon,
  title,
  badge,
  children,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <div className={cx('mb-8', styles.cardItemTitle)}>
        <span>{icon}</span>
        <h3 className="body-prominent">{title}</h3>
        {badge}
      </div>

      <div className={cx('body-standard', styles.cardItemText)}>{children}</div>
    </div>
  );
}

function Card({
  illustration,
  title,
  children,
  button,
}: {
  illustration: React.ReactNode;
  title: React.ReactNode;
  children: React.ReactNode;
  button: React.ReactNode;
}): JSX.Element {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardIllustration}>{illustration}</div>
        <h2 className={cx('body-prominent', styles.cardTitle)}>{title}</h2>
      </div>

      <div>{children}</div>

      <div>{button}</div>
    </div>
  );
}

export function LandingDesktop({
  hasGermanResidencePermit,
  onContinuePhone,
  onContinueComputer,
  onBack,
  trackingPrefix,
}: {
  hasGermanResidencePermit: boolean;
  onContinuePhone: () => void;
  onContinueComputer: () => void;
  onBack?: () => void;
  trackingPrefix: string;
}): React.JSX.Element {
  const intl = useIntl();
  const idnowWebsiteHref = intl.formatMessage({
    id: 'identity-verification-kit.links.idnowWebsite',
  });
  const idnowTermsHref = intl.formatMessage({ id: 'identity-verification-kit.links.idnowTerms' });
  const idnowPrivacyHref = intl.formatMessage({
    id: 'identity-verification-kit.links.idnowPrivacy',
  });
  useTrackRender({ eventName: `${trackingPrefix}_idnow_verification_screen_loaded` });

  return (
    <div className={styles.container}>
      <div>
        <h1 className="heading-section mb-16">
          <FormattedMessage
            defaultMessage="Verify your identity"
            id="identity-verification-kit.qonto-idnow.intro.title"
          />
        </h1>
        <p className="body-standard mb-32">
          {hasGermanResidencePermit ? (
            <FormattedMessage
              id="identity-verification-kit.qonto-idnow.computer.intro.residence-permit.subtitle"
              values={{
                idnowLink: (chunks: React.ReactNode) => (
                  <a
                    className="body-standard-link"
                    href={idnowWebsiteHref}
                    rel="noopener"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="identity-verification-kit.qonto-idnow.computer.intro.subtitle"
              values={{
                idnowLink: (chunks: React.ReactNode) => (
                  <a
                    className="body-standard-link"
                    href={idnowWebsiteHref}
                    rel="noopener"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
                idnowTermsLink: (chunks: React.ReactNode) => (
                  <a
                    className="body-standard-link"
                    href={idnowTermsHref}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
                idnowPrivacyLink: (chunks: React.ReactNode) => (
                  <a
                    className="body-standard-link"
                    href={idnowPrivacyHref}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          )}
        </p>

        <div className={cx(styles.cards, onBack !== undefined && 'mb-32')}>
          <Card
            button={
              <Button data-test-continue-phone onPress={onContinuePhone} stretch>
                <FormattedMessage
                  defaultMessage="Continue on phone"
                  id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.cta"
                />
              </Button>
            }
            illustration={<PhoneVerification />}
            title={
              <FormattedMessage
                defaultMessage="Verification on a phone"
                id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.title"
              />
            }
          >
            <CardItem
              badge={
                <BadgeHighlight
                  text={intl.formatMessage({
                    id: 'identity-verification-kit.qonto-idnow.computer.intro.phone-verification.eID.badge',
                    defaultMessage: 'Fastest',
                  })}
                />
              }
              icon={<IconScanOutlined />}
              title={
                <FormattedMessage
                  defaultMessage="eID scan"
                  id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.eID.title"
                />
              }
            >
              <FormattedMessage
                defaultMessage="Scan your eID card on your phone without an IDnow agent."
                id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.eID.subtitle"
              />
            </CardItem>

            <Divider />

            <CardItem
              icon={<IconHeadsetOutlined />}
              title={
                <FormattedMessage
                  defaultMessage="Video call"
                  id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.video-call.title"
                />
              }
            >
              <FormattedMessage
                defaultMessage="Start a video call your phone with an IDnow agent."
                id="identity-verification-kit.qonto-idnow.computer.intro.phone-verification.video-call.subtitle"
              />
            </CardItem>
          </Card>

          <Card
            button={
              <Button data-test-continue-computer onPress={onContinueComputer} stretch>
                <FormattedMessage
                  defaultMessage="Continue on computer"
                  id="identity-verification-kit.qonto-idnow.computer.intro.computer-verification.cta"
                />
              </Button>
            }
            illustration={<ComputerVerification />}
            title={
              <FormattedMessage
                defaultMessage="Verification on a computer"
                id="identity-verification-kit.qonto-idnow.computer.intro.computer-verification.title"
              />
            }
          >
            <CardItem
              icon={<IconHeadsetOutlined />}
              title={
                <FormattedMessage
                  defaultMessage="Video call"
                  id="identity-verification-kit.qonto-idnow.computer.intro.computer-verification.video-call.title"
                />
              }
            >
              <FormattedMessage
                defaultMessage="Start a video call on your computer with an IDnow agent. Make sure your microphone and camera are activated."
                id="identity-verification-kit.qonto-idnow.computer.intro.computer-verification.video-call.subtitle"
              />
            </CardItem>
          </Card>
        </div>
      </div>

      {onBack !== undefined && <BackButtonSimple data-test-back onPress={onBack} />}
    </div>
  );
}
